<template>
    <div class="input-group">
      <div class="input-group-prepend">
        <span class="input-group-addon input-address-prepend">
          <i class="far fa-map-marked" v-if="!geocodingAddress"></i>
          <i class="fas fa-check-circle input-address-result-icon result-fg-success" v-if="geocodeSuccess"></i>
          <i class="fas fa-times-circle input-address-result-icon result-fg-failed" v-if="geocodeError"></i>
          <Spinner v-if="geocodingAddress" small inline />
        </span>
      </div>
      <input 
        ref="inputField"
        v-bind:type="type"
        :class="['form-control', variant, hasError() ? 'is-invalid' : '']"
        :width="width"
        v-bind:value="value"
        v-on:input="inputUpdated"
        v-bind:placeholder="placeholder"
        :maxlength="maxlength"
        @keypress="onKeypress"
        @keydown.enter="onEnterPressed"
        @keydown.tab="$emit('tab', $event)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @click="$emit('click', $event)"
        :readonly="readonly" />
      <div v-if="addon || addonSlot" class="input-group-append">
        <span class="input-group-addon" v-if="addon" v-html="addon" />
        <slot name="addon"></slot>
      </div>
      <div class="form-invalid-feedback" v-if="hasError() || geocodeError">
        {{getError()}}{{geocodeError}}
      </div>
      <div v-if="!hasError() && note" class="form-field-note">{{note}}</div>
    </div>
</template>

<style scoped>
.input-address-prepend {
  position: relative;
}
.input-address-result-icon {
  position: absolute;
  right: 4px;
  bottom: 4px;
  font-size: 11px;
}
</style>

<script>
import _ from 'lodash'
import formField from '@/mixins/formField'

export default {
  name: 'FormInputAddress',
  mixins: [formField],
  props: {
    type: {
      type: String,
      default: 'text'
    },
    geocodeAddress: {
      type: Boolean,
      default: false,
    },
    variant: String,
    value: [String, Number],
    placeholder: String,
    field: String,
    note: String,
    addon: String,
    addonSlot: Boolean,
    prepend: String,
    prependSlot: Boolean,
    readonly: Boolean,
    wholeNumber: {
      type: Boolean,
      default: false
    },
    width: {
      type: Number,
      default: 20
    },
    maxlength: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      geocoder: null,
      geocodingAddress: false,
      geocodeSuccess: false,
      geocodeError: '',
    }
  },
  watch: {
    value: function(newVal, oldVal) {
      // Reset error when typing if the field was blank
      // Not 100% fix to clearing errors (eg, some errors may be logic based) but helpful
      if(oldVal == '' || oldVal == null)
        this.resetFieldError(this.$props.field)

      // Default to empty string if null given
      if(newVal == null) {
        this.$nextTick(() => {
          this.$emit('input', '')
          this.$emit('geocodeUpdate', null)
        })
        return
      }

      this._debouncedGeocodeAddress()      
    }
  },
  created() {
    this._debouncedGeocodeAddress = _.debounce(this._addressUpdated, 1000)
  },
  mounted() {
    this._addressUpdated()
  },
  methods: {
    hasError() {
      return this.getFieldError(this.$props.field).length > 0
    },
    getError() {
      return this.getFieldError(this.$props.field)
    },
    focus() {
      this.$refs.inputField.focus()
    },
    select() {
      this.$refs.inputField.select()
    },
    setSelectionRange(selectionStart, selectionEnd) {
      this.$refs.inputField.setSelectionRange(selectionStart, selectionEnd)
    },
    onEnterPressed(ev) {
      this.$emit('enter', ev)      
    },
    inputUpdated(evt) {
      this.$emit('input', evt.target.value)
    },
    onKeypress(evt) {
      if(this.wholeNumber) {
        // Disallow decimal places for whole numbers
        let keyCode = (evt.keyCode ? evt.keyCode : evt.which)
        if(keyCode === 46)
          return evt.preventDefault()
      }
    },
    _addressUpdated() {      
      if(!this.geocoder) {
        this.$mapService.loadGoogleMaps().then(() => {
          if(!this.$mapService.googleMapAPI)
            return
            
          this.geocoder = new this.$mapService.googleMapAPI.maps.Geocoder()

          this._geocodeAddress()
        })
      } else {
        this._geocodeAddress()
      }
    },
    _geocodeAddress() {
      this.geocodingAddress = false
      this.geocodeSuccess = false
      this.geocodeError = false

      let address = (this.value || '').trim()
      if(address == '') {
        this.$emit('geocodeUpdate', null)
        return
      }

      if(this.geocoder) {
        this.geocodingAddress = true

        let self = this
        this.geocoder.geocode({ 'address': address }, function(results, status) {
          if(status == self.$mapService.googleMapAPI.maps.GeocoderStatus.OK) {
            self.$emit('geocodeUpdate', { lng: results[0].geometry.location.lng(), lat: results[0].geometry.location.lat() })
            self.geocodeSuccess = true
          } else {
            self.$emit('geocodeUpdate', null)
            self.geocodeError = 'Address Error'
            if(status == self.$mapService.googleMapAPI.maps.GeocoderStatus.ZERO_RESULTS)
              self.geocodeError += ': Could not find address'
            else
              self.geocodeError += ': Error calculating address location. Try again'
          }

          self.geocodingAddress = false
        })
      }
    }
  }
};
</script>